import { clearUndefinedKeys } from '../utils/utils';
import { AccountService } from './AccountService';

export interface BaseDataLayerPayload {}

export interface DataLayerUserPayload extends BaseDataLayerPayload {
  providerName?: string;
  vendorName?: string;
  countryCode?: string;
  cityId?: number;
  providerId?: number;
  userEmail: string;
}

export interface PageChangePayload extends DataLayerUserPayload {
  url: string;
}

export interface MenuCategoryChangePayload extends DataLayerUserPayload {
  id: number;
  name: string;
}

export interface OrderHistoryRefundsFilterChangePayload extends DataLayerUserPayload {
  value: boolean;
}

export interface SignupSubmissionPayload extends BaseDataLayerPayload {
  continue_token: string;
  stepId: string;
  language?: string;
  country?: string;
  city?: string;
  sha256_email_address?: string;
  sha256_phone_number?: string;
  sha256_first_name?: string;
  sha256_last_name?: string;
}

export enum EventType {
  PageChange = 'PageChange',
  MenuCategoryChange = 'MenuCategoryChange',
  OrderHistoryRefundsFilterChange = 'OrderHistoryRefundsFilterChange',

  SignupLeadCreated = 'SignupLeadCreated',
  SignupLeadUpdated = 'SignupLeadUpdated',
  SignupLeadSubmitted = 'SignupLeadSubmitted',
}

export interface EventPayloadMap {
  [EventType.PageChange]: PageChangePayload;
  [EventType.MenuCategoryChange]: MenuCategoryChangePayload;
  [EventType.OrderHistoryRefundsFilterChange]: OrderHistoryRefundsFilterChangePayload;

  [EventType.SignupLeadCreated]: SignupSubmissionPayload;
  [EventType.SignupLeadUpdated]: SignupSubmissionPayload;
  [EventType.SignupLeadSubmitted]: SignupSubmissionPayload;
}

interface EventData<Event extends keyof EventPayloadMap> {
  event: Event;
  payload: EventPayloadMap[Event];
}

export class TrackingService {
  constructor(private readonly accountService: AccountService) {}

  private pushEvent<Event extends keyof EventPayloadMap>(
    eventData: EventData<Event>,
    delayEvent: boolean = false,
  ) {
    const layer = window.dataLayer;
    if (!layer) {
      return;
    }

    const cleanEventData = {
      ...eventData,
      ...clearUndefinedKeys(eventData.payload),
    };

    // Timeout is required in order for the generated event to include
    // the updated document title, as the title update is hooked to
    // location.pathname updates (just like this event creation), and
    // cannot be easily prioritised before this event
    if (!delayEvent) {
      layer.push(cleanEventData);
    } else {
      setTimeout(() => layer.push(cleanEventData), 10);
    }
  }

  public pushPageChangeEvent(payload: Omit<PageChangePayload, keyof DataLayerUserPayload>) {
    return this.pushEvent(
      { event: EventType.PageChange, payload: this.payloadToUserPayload(payload) },
      true,
    );
  }

  public pushMenuCategoryChangeEvent(
    payload: Omit<MenuCategoryChangePayload, keyof DataLayerUserPayload>,
  ) {
    return this.pushEvent({
      event: EventType.MenuCategoryChange,
      payload: this.payloadToUserPayload(payload),
    });
  }

  public pushSignupLeadCreatedEvent(payload: SignupSubmissionPayload) {
    return this.pushEvent({ event: EventType.SignupLeadCreated, payload });
  }

  public pushSignupLeadUpdatedEvent(payload: SignupSubmissionPayload) {
    return this.pushEvent({ event: EventType.SignupLeadUpdated, payload });
  }

  public pushSignupLeadSubmittedEvent(payload: SignupSubmissionPayload) {
    return this.pushEvent({ event: EventType.SignupLeadSubmitted, payload });
  }

  private payloadToUserPayload<T extends BaseDataLayerPayload>(
    payload: T,
  ): T & DataLayerUserPayload {
    const accountData = this.accountService.getSelectedProviderData$().state;
    const userDetails = this.accountService.getAccountDetails$().state;

    return {
      providerName: accountData?.providerName,
      vendorName: accountData?.vendorName,
      countryCode: accountData?.countryCode,
      cityId: accountData?.cityId,
      providerId: accountData?.providerId,
      userEmail: userDetails.account_data.email,
      ...payload,
    };
  }
}
